import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, message, notification, Empty } from "antd";
import {
  CloseCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import legalIcon from "../../../../Assets/Icons/local_gas_station.svg";
import dateIcon from "../../../../Assets/Icons/calendar_month.svg";
import regIcon from "../../../../Assets/Icons/markdown_copy.svg";
import currencyIcon from "../../../../Assets/Icons/attach_money.svg";
import contactIcon from "../../../../Assets/Icons/account_child_invert.svg";
import mailIcon from "../../../../Assets/Icons/mail.svg";
import locIcon from "../../../../Assets/Icons/distance.svg";
import bankIcon from "../../../../Assets/Icons/account_balance.svg";
import addressIcon from "../../../../Assets/Icons/pin_drop.svg";
import holderIcon from "../../../../Assets/Icons/account_box.svg";
import accIcon from "../../../../Assets/Icons/numbers.svg";
import typeIcon from "../../../../Assets/Icons/contact_emergency.svg";
import bicIcon from "../../../../Assets/Icons/password_2.svg";
import paymentIcon from "../../../../Assets/Icons/credit_card_type.svg";
import brandIcon from "../../../../Assets/Icons/storefront.svg";
import vendorTypeIcon from "../../../../Assets/Icons/vendor_type.svg";
import phoneIcon from "../../../../Assets/Icons/call.svg";
import stationIcon from "../../../../Assets/Icons/station_name.svg";
import fuelBrandIcon from "../../../../Assets/Icons/local_gas_station_black.svg";
import wipayAccountIcon from "../../../../Assets/Icons/wipay_account.svg";
import wipayApiIcon from "../../../../Assets/Icons/wipay_API.svg";
import "./style.css";
import FuelTypeCard from "../../../../Components/Cards/FuelTypeCard";
import GenericOperatingHours from "../../../../Components/Tables/GenericOperatingHourTables";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import {
  getApprovalById,
  rejectApproval,
  approveApproval,
  requestRevision,
} from "../../../../API/Admin/Approval&Requests";
import { getDocumentCategories } from "../../../../API/Config";
import usePageAccess from "../../../../Utlis/usePageAccess";
import { getInitials } from "../../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-US");
};

const formatPhone = (phone) => {
  if (!phone || phone === "-") return "-";
  return phone.startsWith("+") ? phone : `+${phone}`;
};

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const VendorOnboarding = () => {
  const { canEdit } = usePageAccess("approvals-requests");
  const navigate = useNavigate();
  const location = useLocation();
  const passedId = location.state?.id;
  const [operatingHours, setOperatingHours] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [fuelTypes, setFuelTypes] = useState([]);
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [requestingRevision, setRequestingRevision] = useState(false);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [request, setRequest] = useState({
    company: "",
    legalName: "-",
    brandName: "-",
    dateIncorporated: "-",
    taxId: "-",
    vendorType: "-",
    currency: "-",
    contactManager: "-",
    contactEmail: "-",
    companyContactNumber: "-",
    phoneNumber: "-",
    location: "-",
    stationName: "-",
    stationId: "-",
    stationAddress: "-",
    fuelBrand: "-",
    latitude: "-",
    longitude: "-",
    status: "-",
    avatar: null,
  });

  useEffect(() => {
    if (!passedId) {
      message.error(
        "No request selected. Please open this page from the Approvals & Requests list.",
      );
      return;
    }

    const fetchApproval = async () => {
      try {
        const res = await getApprovalById(passedId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          const company = data.entity || {};
          const owner = data.submitted_by || {};

          setRequest({
            company: company.legal_name || company.brand_name || "-",
            legalName: company.legal_name || "-",
            brandName: company.brand_name || "-",
            dateIncorporated: formatDate(company.date_incorporated),
            taxId: company.tax_id || "-",
            vendorType: company.vendor_type || "-",
            currency: company.currency || "-",
            contactManager:
              owner.name || company.primary_contact_person || "-",
            contactEmail: company.contact_email || owner.email || "-",
            companyContactNumber: formatPhone(
              company.registration_contact_phone,
            ),
            phoneNumber: formatPhone(company.contact_phone),
            location: company.address || "-",
            stationName: company.station_name || "-",
            stationId: company.station_id || "-",
            stationAddress: company.station_address || "-",
            fuelBrand:
              company.fuel_brand?.name || company.fuel_brand || "-",
            latitude: company.latitude ?? "-",
            longitude: company.longitude ?? "-",
            status: data.status || "-",
            avatar: resolveFileUrl(company.avatar),
          });

          setFuelTypes(
            Array.isArray(company.fuel_types_offered)
              ? company.fuel_types_offered.map((fuel) => fuel.name)
              : [],
          );

          if (Array.isArray(company.operating_hours)) {
            setOperatingHours(company.operating_hours);
          }

          const bank = company.bank_details || {};
          setBankDetails({
            bankName: bank.bank_name || "-",
            bankAddress: bank.bank_address || "-",
            accountHolderName: bank.account_holder_name || "-",
            accountNumber: bank.account_number || "-",
            accountType: bank.account_type || "-",
            swiftCode: bank.swift_code || "-",
            wipayAccountNumber: company.wipay_account_number || "-",
            wipayApiKey: company.wipay_api_key || "-",
          });

          if (Array.isArray(company.documents)) {
            setDocuments(
              company.documents.map((doc) => ({
                key: String(doc.id),
                name: doc.file?.split("/").pop() || doc.category || "-",
                category: doc.category || "-",
                allowedFormats: doc.allowed_formats || [],
                date: formatDate(doc.uploaded_at),
                expiryDate: formatDate(doc.expiry_date),
                status: "Pending",
                fileUrl: resolveFileUrl(doc.file),
              })),
            );
          }
        } else {
          message.error(res?.data?.message || "Failed to load request.");
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load request.",
        );
      }
    };

    fetchApproval();
  }, [passedId]);

  useEffect(() => {
    const fetchDocumentCategories = async () => {
      const res = await getDocumentCategories("fuel-vendor");
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setDocumentCategories(
          list.map((cat) => ({
            label: cat.document_name,
            value: cat.document_name,
            allowedFormats: cat.allowed_formats,
          })),
        );
      }
    };

    fetchDocumentCategories();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const statusMap = {
    approved: "status-approved",
    rejected: "status-denied",
    denied: "status-denied",
    pending: "status-pending",
    revision_requested: "status-revision-requested",
  };
  const currentStatus = request.status;
  const formatStatus = (value) =>
    value
      ? value
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : value;

  const [documents, setDocuments] = useState([]);

  const [bankDetails, setBankDetails] = useState({
    bankName: "-",
    bankAddress: "-",
    accountHolderName: "-",
    accountNumber: "-",
    accountType: "-",
    swiftCode: "-",
    wipayAccountNumber: "-",
    wipayApiKey: "-",
  });

  const handleReject = async () => {
    if (!remarks.trim()) {
      notification.warning({
        message: "Audit Notes Required",
        description: "Please enter audit notes before rejecting this vendor.",
      });
      return;
    }

    if (!passedId) {
      message.error("No request selected.");
      return;
    }

    setRejecting(true);
    try {
      const res = await rejectApproval(passedId, remarks);

      if (Math.floor(res?.status / 100) === 2) {
        notification.error({
          message: res?.data?.message || "Vendor Rejected",
        });
        navigate("/approvals-requests");
      } else {
        message.error(res?.data?.message || "Failed to reject request.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to reject request.",
      );
    } finally {
      setRejecting(false);
    }
  };

  const handleRevision = async () => {
    if (!remarks.trim()) {
      notification.warning({
        message: "Audit Notes Required",
        description: "Please enter audit notes before requesting revision.",
      });
      return;
    }

    if (!passedId) {
      message.error("No request selected.");
      return;
    }

    setRequestingRevision(true);
    try {
      const res = await requestRevision(passedId, remarks);

      if (Math.floor(res?.status / 100) === 2) {
        const devToken = res?.data?.data?.dev_revision_token;
        notification.success({
          message: res?.data?.message || "Revision Requested",
        });
        navigate("/approvals-requests");
      } else {
        message.error(res?.data?.message || "Failed to request revision.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to request revision.",
      );
    } finally {
      setRequestingRevision(false);
    }
  };

  const handleApprove = async () => {
    if (!remarks.trim()) {
      notification.warning({
        message: "Audit Notes Required",
        description: "Please enter audit notes before approving this vendor.",
      });
      return;
    }

    if (!passedId) {
      message.error("No request selected.");
      return;
    }

    setApproving(true);
    try {
      const res = await approveApproval(passedId, remarks);

      if (Math.floor(res?.status / 100) === 2) {
        notification.success({
          message: res?.data?.message || "Vendor Approved",
        });
        navigate("/approvals-requests");
      } else {
        message.error(res?.data?.message || "Failed to approve request.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to approve request.",
      );
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="vendor-onboard">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="vendor-header-card">
        <div className="vendor-info-wrapper">
          {request.avatar ? (
            <img
              src={request.avatar}
              alt="Company Logo"
              className="vendor-logo"
            />
          ) : (
            <div
              className="vendor-logo vendor-logo-fallback"
              style={{ background: getAvatarColor(request.company) }}
            >
              {getInitials(request.company)}
            </div>
          )}

          <div className="vendor-details">
            <div className="company-name">{request.company}</div>
            <div className={`status-badge ${statusMap[currentStatus]}`}>
              {formatStatus(currentStatus)}
            </div>
          </div>
        </div>
      </div>
      <div className="info">
        <div className="info-title">
          Fuel Vendors & Contact Information (Summary)
        </div>
        <div className="info-card">
          <div className="info-grid">
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={legalIcon} alt="Fuel Icon" />
                </div>
                <div className="info-label">Legal Entity Name</div>
              </div>
              <div className="info-value">{request.legalName}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={brandIcon} alt="Brand Icon" />
                </div>
                <div className="info-label">Brand Name</div>
              </div>
              <div className="info-value">{request.brandName}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={dateIcon} alt="Date Icon" />
                </div>
                <div className="info-label">Date Incorporated</div>
              </div>
              <div className="info-value">{request.dateIncorporated}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={regIcon} alt="Fuel Icon" />
                </div>
                <div className="info-label">Tax ID / EIN</div>
              </div>
              <div className="info-value">{request.taxId}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={vendorTypeIcon} alt="Vendor Type Icon" />
                </div>
                <div className="info-label">Vendor Type</div>
              </div>
              <div className="info-value">{request.vendorType}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={currencyIcon} alt="Curr Icon" />
                </div>
                <div className="info-label">Currency</div>
              </div>
              <div className="info-value">{request.currency}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={contactIcon} alt="Curr Icon" />
                </div>
                <div className="info-label">Contact Manager</div>
              </div>
              <div className="info-value">{request.contactManager}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={mailIcon} alt="Curr Icon" />
                </div>
                <div className="info-label">Contact Email</div>
              </div>
              <div className="info-value">{request.contactEmail}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={phoneIcon} alt="Phone Icon" />
                </div>
                <div className="info-label">Company Contact Number</div>
              </div>
              <div className="info-value">{request.companyContactNumber}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={phoneIcon} alt="Phone Icon" />
                </div>
                <div className="info-label">Phone Number</div>
              </div>
              <div className="info-value">{request.phoneNumber}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={locIcon} alt="lov Icon" />
                </div>
                <div className="info-label">Location</div>
              </div>
              <div className="info-value">{request.location}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="info">
        <div className="info-title">Fuel Station Information</div>
        <div className="info-card">
          <div className="info-grid">
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={stationIcon} alt="Station Icon" />
                </div>
                <div className="info-label">Station Name</div>
              </div>
              <div className="info-value">{request.stationName}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={accIcon} alt="Station Id Icon" />
                </div>
                <div className="info-label">Station ID</div>
              </div>
              <div className="info-value">{request.stationId}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={addressIcon} alt="Station Address Icon" />
                </div>
                <div className="info-label">Station Address</div>
              </div>
              <div className="info-value">{request.stationAddress}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={fuelBrandIcon} alt="Fuel Brand Icon" />
                </div>
                <div className="info-label">Fuel Brand</div>
              </div>
              <div className="info-value">{request.fuelBrand}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={locIcon} alt="Latitude Icon" />
                </div>
                <div className="info-label">Latitude</div>
              </div>
              <div className="info-value">{request.latitude}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={locIcon} alt="Longitude Icon" />
                </div>
                <div className="info-label">Longitude</div>
              </div>
              <div className="info-value">{request.longitude}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="info">
        <div className="info-title">Banking & Settlement</div>
        <div className="info-card">
          <div className="info-grid">
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={bankIcon} alt="bank Icon" />
                </div>
                <div className="info-label">Bank Name</div>
              </div>
              <div className="info-value">{bankDetails.bankName}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={addressIcon} alt="address Icon" />
                </div>
                <div className="info-label">Bank Address</div>
              </div>
              <div className="info-value">{bankDetails.bankAddress}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={holderIcon} alt="holder Icon" />
                </div>
                <div className="info-label">Account Holder Name</div>
              </div>
              <div className="info-value">{bankDetails.accountHolderName}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={accIcon} alt="Acc Icon" />
                </div>
                <div className="info-label">Account Number</div>
              </div>
              <div className="info-value">{bankDetails.accountNumber}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={typeIcon} alt="type Icon" />
                </div>
                <div className="info-label">Account Type</div>
              </div>
              <div className="info-value">{bankDetails.accountType}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={bicIcon} alt="Curr Icon" />
                </div>
                <div className="info-label">SWIFT / BIC Code</div>
              </div>
              <div className="info-value">{bankDetails.swiftCode}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={wipayAccountIcon} alt="WiPay Account Icon" />
                </div>
                <div className="info-label">WiPay Account Number</div>
              </div>
              <div className="info-value">{bankDetails.wipayAccountNumber}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={wipayApiIcon} alt="WiPay API Icon" />
                </div>
                <div className="info-label">WiPay API Key</div>
              </div>
              <div className="info-value">{bankDetails.wipayApiKey}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="fuel-type-card">
        {fuelTypes.length > 0 ? (
          <FuelTypeCard
            fuelList={fuelTypes}
            selectedFuel={fuelTypes}
            disabled={true}
          />
        ) : (
          <div
            style={{
              background: "var(--white)",
              border: "1px solid var(--stroke-2, #dbe5e5)",
              borderRadius: 16,
              padding: 24,
            }}
          >
            <Empty description="No Fuel Type data available" />
          </div>
        )}
      </div>

      <div>
        {operatingHours.length > 0 ? (
          <GenericOperatingHours
            value={operatingHours}
            onChange={(data) => setOperatingHours(data)}
            disabled={true}
          />
        ) : (
          <div
            style={{
              background: "var(--white)",
              border: "1px solid var(--stroke-2, #dbe5e5)",
              borderRadius: 16,
              padding: 24,
            }}
          >
            <Empty description="No Operating Hours data available" />
          </div>
        )}
      </div>

      <div>
        <GenericDocumentManagerTable
          initialDocuments={documents}
          documentTypes={documentCategories}
          showTableOnly={true}
          hideRemoveAction={true}
        />
      </div>

      {canEdit &&
        ["pending", "revision_requested"].includes(
          String(currentStatus).toLowerCase(),
        ) && (
        <div className="decision-panel">
          <div className="info-title">Decision Panel</div>
          <div className="decision-card">
            <div className="audit-section">
              <label>Audit Notes</label>
              <textarea
                placeholder="Enter a description..."
                className="audit-textarea"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
            <div className="decision-actions">
              <Button
                className="reject-btn"
                icon={<CloseCircleOutlined />}
                onClick={handleReject}
                loading={rejecting}
                disabled={rejecting}
              >
                Reject Vendor
              </Button>

              <Button
                className="revision-btn"
                icon={<ReloadOutlined />}
                onClick={handleRevision}
                loading={requestingRevision}
                disabled={requestingRevision}
              >
                Request Revision
              </Button>

              <Button
                type="primary"
                className="approve-btn"
                icon={<CheckCircleOutlined />}
                onClick={handleApprove}
                loading={approving}
                disabled={approving}
              >
                Approve Vendor
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorOnboarding;
